import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { GlowWrapper } from "@/components/ui/glow-wrapper";

export function RoadmapSection() {
  const phases = [
    {
      id: "phase1",
      status: "🟢",
      statusText: "NOW LIVE - CLOSED BETA",
      title: "PHASE 1: CURALYSIS PATIENT PLATFORM",
      description: "Research-grade tracking for MND, Parkinson's, and epilepsy",
      features: [
        "✓ Daily symptom logs, medication tracking, clinical scales",
        "✓ Condition-specific features:",
        "• MND: ALSFRS-R tracking, bulbar function monitoring",
        "• Parkinson's: MDS-UPDRS, medication timing, motor diaries",
        "• Epilepsy: Seizure tracking, triggers, EEG timeline integration",
        "✓ Timeline visualization & research-ready data export",
        "✓ 71 patients ready to onboard from StackDat community"
      ],
      ctas: ["Join Patient Beta", "Read Dr. Visser's Endorsement"],
      color: "teal",
      primaryColor: "#14B8A6",
      gradientColors: ["rgba(20, 184, 166, 0.35)", "rgba(6, 182, 212, 0.30)"]
    },
    {
      id: "phase1b",
      status: "🔵",
      statusText: "IN DEVELOPMENT - MNDA SA PARTNERSHIP",
      title: "PHASE 1B: ORGANISATION DASHBOARDS",
      description: "Clinician and adviser tools for patient monitoring",
      features: [
        "→ Real-time dashboards for MNDA SA advisers",
        "→ Visit logging, ALSFRS-R updates, connection requests",
        "→ Admin views for service coordinators",
        "→ Co-designed with Dr. Lienka (MNDA SA Clinical Lead)",
        "Expected Beta: Q1 2026"
      ],
      ctas: [],
      color: "cyan",
      primaryColor: "#06B6D4",
      gradientColors: ["rgba(6, 182, 212, 0.35)", "rgba(8, 145, 178, 0.30)"]
    },
    {
      id: "phase2",
      status: "🟡",
      statusText: "2026",
      title: "PHASE 2: EXPANDING THE PLATFORM",
      description: "Growing to more neurological conditions and features",
      features: [
        "→ Additional condition modules (under evaluation)",
        "→ Cross-condition research data insights",
        "→ Enhanced clinical integration tools"
      ],
      ctas: [],
      color: "cyan-purple",
      primaryColor: "linear-gradient(135deg, #06B6D4, #A855F7)",
      gradientColors: ["rgba(6, 182, 212, 0.30)", "rgba(139, 92, 246, 0.25)"]
    },
    {
      id: "phase3",
      status: "🟣",
      statusText: "2026-2027",
      title: "PHASE 3: ELEVITA AI - EMERGENCY TRIAGE ASSISTANT",
      description: "Intelligent support for acute healthcare decisions",
      features: [
        "→ Dynamic symptom assessment beyond standard checklists",
        "→ Automatic escalation triggers (000 alerts, hospital warnings)",
        "→ Pre-triage information for healthcare professionals"
      ],
      ctas: [],
      color: "purple",
      primaryColor: "#A855F7",
      gradientColors: ["rgba(168, 85, 247, 0.35)", "rgba(147, 51, 234, 0.30)"]
    },
    {
      id: "phase4",
      status: "🔮",
      statusText: "2027+",
      title: "PHASE 4: INTEGRATED CARE ECOSYSTEM",
      description: "Connecting patients, clinicians, and researchers across the continuum",
      features: [
        "→ Post-discharge recovery tracking",
        "→ Medication reminder systems",
        "→ Seamless provider communication",
        "→ Longitudinal research data partnerships"
      ],
      ctas: [],
      color: "deep-purple",
      primaryColor: "#9333EA",
      gradientColors: ["rgba(147, 51, 234, 0.35)", "rgba(126, 34, 206, 0.30)"]
    }
  ];

  const getStatusColor = (color: string) => {
    switch (color) {
      case "teal": return "text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-800";
      case "purple": return "text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800";
      default: return "text-gray-600 dark:text-gray-400";
    }
  };

  const getBgColor = (color: string) => {
    switch (color) {
      case "teal": return "bg-gradient-to-br from-teal-50/80 to-teal-100/60 dark:from-teal-950/80 dark:to-teal-900/60";
      case "purple": return "bg-gradient-to-br from-purple-50/80 to-purple-100/60 dark:from-purple-950/80 dark:to-purple-900/60";
      default: return "bg-gray-50 dark:bg-gray-950";
    }
  };

  return (
    <section id="roadmap" className="py-20 relative z-10 bg-white dark:bg-black">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-16 text-black dark:text-white"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            OUR JOURNEY: FROM PATIENT <span className="text-teal-600 dark:text-teal-400">TRACKING TO INTEGRATED CARE</span>
          </h2>
          <div className="w-24 h-1 bg-teal-600 dark:bg-teal-400 mx-auto mb-8"></div>
        </motion.div>

        <div className="space-y-8">
          {phases.map((phase, index) => (
            <motion.div
              className="p-8 relative overflow-hidden rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${phase.gradientColors[0]} 0%, ${phase.gradientColors[1]} 100%)`,
                backdropFilter: 'blur(10px)',
                border: phase.color === "teal" ? '2px solid rgba(20, 184, 166, 0.4)' :
                        phase.color === "cyan" ? '2px solid rgba(6, 182, 212, 0.4)' :
                        phase.color === "cyan-purple" ? '2px solid rgba(99, 102, 241, 0.3)' :
                        phase.color === "purple" ? '2px solid rgba(169, 85, 247, 0.48)' :
                        '2px solid rgba(147, 51, 234, 0.4)',
                boxShadow: phase.color === "teal" ? '0 8px 32px rgba(20, 184, 166, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)' :
                         phase.color === "cyan" ? '0 8px 32px rgba(6, 182, 212, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)' :
                         phase.color === "cyan-purple" ? '0 8px 32px rgba(99, 102, 241, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.06)' :
                         phase.color === "purple" ? '0 8px 32px rgba(169, 85, 247, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)' :
                         '0 8px 32px rgba(147, 51, 234, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.08)'
              }}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
            >
              {/* Phase Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{phase.status}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-black dark:text-white">
                        {phase.title}
                      </h3>
                      <p className={`text-sm font-semibold ${getStatusColor(phase.color)}`}>
                        {phase.statusText}
                      </p>
                    </div>
                  </div>
                  <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                    {phase.description}
                  </p>
                </div>
              </div>

              {/* Phase Features */}
              <div className="space-y-2 mb-6">
                {phase.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="text-black dark:text-white">
                    {feature.startsWith("•") ? (
                      <p className="ml-6 text-sm text-gray-700 dark:text-gray-300">{feature}</p>
                    ) : (
                      <p className="text-sm text-gray-700 dark:text-gray-300">{feature}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Phase CTAs */}
              {phase.ctas.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {phase.ctas.map((cta, ctaIndex) => (
                    <motion.div
                      key={ctaIndex}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <GlowWrapper color="teal" intensity="medium">
                        <Button
                          asChild
                          variant="default"
                          size="sm"
                          className="text-black"
                        >
                          <a href="#clinical-validation">
                            {cta}
                          </a>
                        </Button>
                      </GlowWrapper>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Decorative element */}
              <motion.div
                className="absolute top-4 right-4 w-16 h-16 rounded-full opacity-20 backdrop-blur-sm"
                style={{ backgroundColor: phase.color === "teal" ? "#14b8a6" : "#8B5CF6" }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.1, 0.2, 0.1]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: index * 0.5
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Join us on this journey to transform healthcare for Australians living with neurological conditions.
          </p>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <GlowWrapper color="teal" intensity="high">
              <Button
                asChild
                variant="default"
                size="lg"
                className="text-black"
              >
                <a href="https://curalysis.com" target="_blank" rel="noopener noreferrer">
                  Join the Curalysis Beta Program
                </a>
              </Button>
            </GlowWrapper>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
